import { useCallback, useContext } from 'react';
import Promise from 'bluebird';
import {
  loadBundleCountries,
  loadCampaign,
  modelToViewModelForCampaign,
  loadAssetGroupWithVideos,
} from '@bingads-webui-campaign-react/perf-max-step-wizard';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { useWizardService } from '@bingads-webui-react/wizard';
import {
  readLocationCriterions,
} from '@bingads-webui-campaign-react/perf-max-data';
import { flowToPlatform } from '@bingads-webui-campaign-react/unified-app-assets';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { createVideoRepository } from '@bingads-webui-campaign-react/asset-library-services';
import { useAppStoreRepository } from '@bingads-webui-campaign-react/app-store-selector';
import { updateAssetGroupAndCampaignStatus } from '@bingads-webui-campaign-react/perf-max-wizard-north-star';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';

export const useSummaryStepRepository = () => {
  const serverContext = useContext(ServerContext);
  const { scope } = useContext(ScopeContext) || {};
  const { cid, aid } = scope.values;
  const { state } = useWizardService();

  const activity = useContext(ActivityContext);

  const i18n = useContext(I18nContext);

  const {
    wizardData: {
      flow,
      isPublishing,
      campaign: savedCampaign,
      assetGroup: savedAssetGroup,
    },
  } = state.context;

  const { searchAppById, oDataConfig } = useAppStoreRepository({ platform: flowToPlatform[flow] });

  const videoRepository = createVideoRepository({
    oDataConfig,
    aid,
    cid,
  });

  const load = useCallback(() => {
    const campaignId = savedCampaign ? savedCampaign.Id : null;
    const assetGroupId = savedAssetGroup ? savedAssetGroup.Id : null;

    const loadPromises = {
      campaign: loadCampaign(cid, aid, campaignId),
      locationTargets: readLocationCriterions(cid, aid, campaignId),
      assetGroup: loadAssetGroupWithVideos(cid, aid, campaignId, assetGroupId, i18n, false, false, videoRepository),
      bundleCountriesAndLocationCriterions: loadBundleCountries({
        cid,
        aid,
        serverContext,
      }),
    };
    return Promise.props(loadPromises).then(({
      bundleCountriesAndLocationCriterions,
      campaign,
      assetGroup,
      locationTargets,
    }) => {
      const campaignData = modelToViewModelForCampaign({
        campaignModel: { ...campaign, Status: savedCampaign.status }, // MT sends UserPaused, if we send this back they mark it as active. So we just use Paused from the saved ad model
        locationCriterionsModel: locationTargets ? locationTargets.value : [],
        bundleCountries: bundleCountriesAndLocationCriterions.bundleCountries,
      });

      return ({
        ...bundleCountriesAndLocationCriterions,
        campaign: campaignData.data,
        assetGroup,
      });
    }).then(({ campaign, assetGroup }) => {
      if (campaign && campaign.CampaignSettings && campaign.CampaignSettings.length > 0) {
        const appSettings = campaign.CampaignSettings.find(settings => settings['@odata.type'] === '#Model.AppSetting');
        const { AppId } = appSettings;

        return searchAppById(AppId).then((appInfo) => {
          // eslint-disable-next-line no-param-reassign
          campaign.AppSetting = appInfo;

          return {
            campaign,
            assetGroup,
          };
        });
      }
      return {
        campaign,
        assetGroup,
      };
    }).catch((errorData) => {
      activity.trace(`[UnifiedAppWizard] loadData failed. Error: ${errorData}`);

      // eslint-disable-next-line prefer-promise-reject-errors
      return Promise.reject({
        error: {
          ...errorData,
        },
      });
    });
  }, [activity, aid, cid, i18n, savedAssetGroup, savedCampaign, searchAppById, serverContext, videoRepository]);

  const save = useCallback(({
    data,
  }) => {
    const { Id: campaignId } = savedCampaign || {};
    const { Id: assetGroupId } = savedAssetGroup || {};

    if (isPublishing) {
      updateAssetGroupAndCampaignStatus({ campaignId, assetGroupId }).then(() => ({
        campaign: data.campaign,
        assetGroup: data.assetGroup,
      })).catch(errorData => (
      // eslint-disable-next-line prefer-promise-reject-errors
        Promise.reject({ error: { ...errorData } })));
    }

    // no update necessary if not publishing.
    return Promise.resolve({
      campaign: data.campaign,
      assetGroup: data.assetGroup,
    });
  }, [isPublishing, savedAssetGroup, savedCampaign]);

  return {
    load,
    save,
  };
};
